import React from 'react';
import { Check, CheckCheck, Clock, AlertCircle } from 'lucide-react';

/**
 * Status da mensagem ENVIADA (icone + texto), COMPARTILHADO pelos 4 inboxes
 * (paridade B6 da matriz de canais, fechada em 2026-09-17). Antes, o WhatsApp
 * QR e o Business mostravam icone MAIS texto ("Lido", "Entregue"); o Instagram
 * e o Messenger mostravam so o icone, com o texto escondido num `title` que so
 * aparece no hover e nao existe no celular.
 *
 * O texto e o mesmo nos 4 de proposito: para o usuario final os inboxes sao o
 * mesmo produto. O que muda por canal e QUAIS estados existem, e isso vem do
 * mapa de capacidade abaixo, num lugar so, em vez de `if canal === 'x'`
 * espalhado pelas telas.
 *
 * EXCECAO DE CANAL (limite real da API, nao divida): o Instagram nao tem
 * evento de entrega. O webhook so trata `read`
 * (routes/instagram/instagram_webhook_routes.py), entao `delivered` nunca
 * acontece ali. Um status de entrega chegando por engano cai em "Enviado", que
 * e a verdade, em vez de prometer entrega que a Meta nao confirmou.
 */

// Nomenclaturas que chegam do banco por canal. A Evolution (QR) usa os ACKs
// em maiuscula; a Cloud API e os canais Meta usam minuscula.
const NORMALIZACAO = {
  READ: 'read',
  DELIVERY_ACK: 'delivered',
  SERVER_ACK: 'sent',
  PENDING: 'pending',
  ERROR: 'failed',
  error: 'failed',
};

const ROTULOS = {
  read: { texto: 'Lido', cor: '#53bdeb' },
  delivered: { texto: 'Entregue', cor: null },
  sent: { texto: 'Enviado', cor: null },
  pending: { texto: 'Enviando…', cor: null },
  failed: { texto: 'Falhou', cor: '#ef4444' },
};

// Estados que cada canal consegue informar. Quem nao esta na lista cai no
// estado imediatamente inferior que o canal conhece.
const ESTADOS_POR_CANAL = {
  wa_personal: ['pending', 'sent', 'delivered', 'read', 'failed'],
  wa_business: ['pending', 'sent', 'delivered', 'read', 'failed'],
  instagram: ['pending', 'sent', 'read', 'failed'],
  messenger: ['pending', 'sent', 'delivered', 'read', 'failed'],
};

const REBAIXAMENTO = { read: 'delivered', delivered: 'sent', sent: 'pending' };

/**
 * Estado efetivo do canal: normaliza a nomenclatura e REBAIXA o que o canal
 * nao consegue informar. `null` quando o status nao e reconhecido — quem
 * chama decide se isso vira "Enviado" ou nada.
 */
export function resolveStatus(status, channel) {
  let estado = NORMALIZACAO[status] || String(status || '').toLowerCase();
  if (!ROTULOS[estado]) return null;
  const permitidos = ESTADOS_POR_CANAL[channel] || ESTADOS_POR_CANAL.wa_business;
  let guarda = 0;
  while (!permitidos.includes(estado) && REBAIXAMENTO[estado] && guarda < 4) {
    estado = REBAIXAMENTO[estado];
    guarda += 1;
  }
  return permitidos.includes(estado) ? estado : null;
}

/**
 * {texto, cor} do status no canal, ou `null` quando nao ha o que mostrar.
 * E daqui que os 4 inboxes tiram a PALAVRA, para ela nao divergir de novo:
 * os dois shells de WhatsApp montam o proprio cabecalho (icone e texto em
 * elementos separados, com cor do tema) e so consomem esta funcao.
 */
export function statusInfo(status, channel) {
  const estado = resolveStatus(status, channel);
  if (!estado) return null;
  const { texto, cor } = ROTULOS[estado];
  return { estado, texto, cor };
}

/** Texto do status no canal ("Lido", "Entregue", ...). Vazio se nao houver. */
export function statusLabel(status, channel) {
  return (statusInfo(status, channel) || {}).texto || '';
}

const MessageStatus = ({ status, channel, size = 13, showText = true, color }) => {
  // Status ausente ou desconhecido numa mensagem NOSSA vale "Enviado": foi o
  // que o Instagram e o Messenger sempre mostraram (check simples).
  const estado = resolveStatus(status, channel) || 'sent';
  const { texto, cor } = ROTULOS[estado];
  const tom = color || cor || 'currentColor';
  const icone = estado === 'failed'
    ? <AlertCircle size={size} color={tom} />
    : estado === 'pending'
      ? <Clock size={size} color={tom} />
      : estado === 'read' || estado === 'delivered'
        ? <CheckCheck size={size} color={tom} />
        : <Check size={size} color={tom} />;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        color: tom, fontSize: 11, lineHeight: 1,
      }}
      title={texto}
    >
      {icone}
      {showText && <span>{texto}</span>}
    </span>
  );
};

export default MessageStatus;
